import type { ReactNode } from 'react'

interface CardProps {
  children: ReactNode
  /** Renders as a button when a press handler is given, so the DOM tells the truth. */
  onPress?: () => void
  padded?: boolean
  className?: string
}

/**
 * A flat card.
 *
 * There are no shadows in this system: depth is the ground → surface → card
 * lightness ladder plus a hairline (design system § Shape and space). A card is
 * therefore `bg-card` + `border-hairline`, and nothing else.
 *
 * SUAP-0103.
 */
export function Card({ children, onPress, padded = true, className = '' }: CardProps) {
  const shared = [
    'block w-full rounded-card border border-hairline bg-card text-left text-ink',
    padded ? 'p-4' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  if (onPress === undefined) {
    return <div className={shared}>{children}</div>
  }

  return (
    <button
      type="button"
      onClick={onPress}
      className={`${shared} min-h-11 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink motion-safe:transition-[background-color,border-color,color] motion-safe:active:bg-surface`}
    >
      {children}
    </button>
  )
}
